/* eslint-disable eqeqeq */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import {
  useGetAllBuildingsById,
  useGetAllLocationsDropdown,
  useGetAllSystemDetailCodesDropdown,
  useGetDocumentIDTypes,
} from "../../hooks/common/query";
import { formattedDate, mapToOptions } from "../../lib/functions";
import debounce from "lodash.debounce";
import i18n from "../../i18n";
import { useGetAllVisitorsReport, useGetVisitorsReportCheckInByDropdown, useGetVisitorsReportCheckOutByDropdown } from "../../hooks/report/query";
import {
  DOC_TYPE_ID,
  EXPORT_PAGE_SIZE,
  SYSTEM_DETAIL_CODES,
} from "../../constants/common";
import { Button } from "../button";
import { ExportVisitorsReportForExcelPDFBody } from "../../hooks/report/interface";

interface SearchVisitorsReportCardProps {
  setAllRequests: Dispatch<SetStateAction<never[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  setExportBody: Dispatch<
    SetStateAction<ExportVisitorsReportForExcelPDFBody | undefined>
  >;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
}

const SearchVisitorsReportCardSchema = Yup.object({
  fullName: Yup.string(),
  companyName: Yup.string(),
  number: Yup.string().matches(
    /^\d{1,14}$/,
    i18n.language === "en" ? "Enter a valid number." : "أدخل رقماً صحيحاً."
  ),
  idType: Yup.string(),
  iqama: Yup.string(),
  eid: Yup.string(),
  passportNumber: Yup.string(),
  visaNumber: Yup.string(),
  checkInDate: Yup.string(),
  checkOutDate: Yup.string(),
  location: Yup.string(),
  building: Yup.string(),
  checkInOutType: Yup.string(),
  visitDurationType: Yup.string(),
  checkInBy: Yup.string(),
  checkOutBy: Yup.string(),
});

const initialState = {
  fullName: "",
  companyName: "",
  number: "",
  location: "",
  building: "",
  checkInDate: "",
  checkOutDate: "",
  visitDurationType: "",
  checkInBy: "",
  checkOutBy: ""
};

const SearchVisitorsReportCard = ({
  setAllRequests,
  page,
  setPages,
  search,
  setCount,
  setExportBody,
  pageSize,
  setPage,
}: SearchVisitorsReportCardProps) => {
  const {
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useFormWithSchema(SearchVisitorsReportCardSchema, {
    defaultValues: {
      checkInDate: formattedDate(new Date()),
      idType: DOC_TYPE_ID.NATIONAL_ID.toString(),
      // location:
      //   user?.n_RoleID !== USER_ROLES_IDS.ADMIN
      //     ? user?.n_LocationID?.toString()
      //       ? user?.n_LocationID?.toString()
      //       : ""
      //     : "",
      location: "",
    },
  });
  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("");

  const { data: allLocations } = useGetAllLocationsDropdown();
  const { data: allCheckInOutTypes } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.CHECK_IN_OUT_TYPES,
  });

  const { data: allVisitDurations } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.VISIT_DURATION_TYPES,
  });

  const { data: allDocumentIDTypes } = useGetDocumentIDTypes();

  const location = watch("location");
  const building = watch("building");
  const fromDate = watch("checkInDate");
  const toDate = watch("checkOutDate");

  const { data: checkInBy } = useGetVisitorsReportCheckInByDropdown({
    encryptedLocationID: location || "",
    n_BuildingID: building ? Number(building) : 0,
    dt_FromDate: fromDate || "",
    dt_ToDate: toDate || "",
  })

  const { data: checkOutBy } = useGetVisitorsReportCheckOutByDropdown({
    encryptedLocationID: location || "",
    n_BuildingID: building ? Number(building) : 0,
    dt_FromDate: fromDate || "",
    dt_ToDate: toDate || "",
  })

  const checkInByReceptionists = useMemo(
    () =>
      mapToOptions(
        "s_Name_En",
        "s_Name_Ar",
        "n_UserID",
        checkInBy
      ),
    [checkInBy]
  );

  const checkOutByReceptionists = useMemo(
    () =>
      mapToOptions(
        "s_Name_En",
        "s_Name_Ar",
        "n_UserID",
        checkOutBy
      ),
    [checkOutBy]
  );

  const documentIDTypes = useMemo(
    () =>
      mapToOptions(
        "s_Desc_E",
        "s_Desc_A",
        "n_DetailedCode",
        allDocumentIDTypes
      ),
    [allDocumentIDTypes]
  );

  const checkInOutTypes = useMemo(
    () =>
      mapToOptions(
        "s_Desc_E",
        "s_Desc_A",
        "n_DetailedCode",
        allCheckInOutTypes
      ),
    [allCheckInOutTypes]
  );

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations
      ),
    [allLocations]
  );

  const fullName = watch("fullName");
  const companyName = watch("companyName");
  const number = watch("number");
  const iqama = watch("iqama");
  const visaNumber = watch("visaNumber");
  const passportNumber = watch("passportNumber");
  const eid = watch("eid");

  const [debouncedVisitorName, setDebouncedFullName] = useState(fullName);
  const [debouncedCompanyName, setDebouncedCompanyName] = useState(companyName);
  const [debouncedNumber, setDebouncedNumber] = useState(number);
  const [debouncedIqama, setDebouncedIqama] = useState(iqama);
  const [debouncedVisaNumber, setDebouncedVisaNumber] = useState(visaNumber);
  const [debouncedPassportNumber, setDebouncedPassportNumber] =
    useState(passportNumber);
  const [debouncedEid, setDebouncedEid] = useState(eid);

  const selectedIdType = watch("idType");

  const checkInOutType = watch("checkInOutType");
  const visitDurationType = watch("visitDurationType");

  const visitDurations = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allVisitDurations),
    [allVisitDurations]
  );

  const debouncedFullNameSetter = useMemo(
    () => debounce((value: string) => setDebouncedFullName(value), 500),
    []
  );

  const debouncedCompanyNameSetter = useMemo(
    () => debounce((value: string) => setDebouncedCompanyName(value), 500),
    []
  );

  const debouncedNumberSetter = useMemo(
    () => debounce((value: string) => setDebouncedNumber(value), 500),
    []
  );

  useEffect(() => {
    setPage(1);
  }, [
    fullName,
    companyName,
    number,
    eid,
    passportNumber,
    visaNumber,
    iqama,
    fromDate,
    toDate,
    visitDurationType,
    building,
    location,
    checkInOutType,
  ]);

  useEffect(() => {
    debouncedFullNameSetter(fullName || "");
    return () => debouncedFullNameSetter.cancel();
  }, [fullName, debouncedFullNameSetter]);

  useEffect(() => {
    debouncedCompanyNameSetter(companyName || "");
    return () => debouncedCompanyNameSetter.cancel();
  }, [companyName, debouncedCompanyNameSetter]);

  useEffect(() => {
    debouncedNumberSetter(number || "");
    return () => debouncedNumberSetter.cancel();
  }, [number, debouncedNumberSetter]);

  const debouncedIqamaSetter = useMemo(
    () => debounce((value: string) => setDebouncedIqama(value), 500),
    []
  );

  useEffect(() => {
    debouncedIqamaSetter(iqama || "");
    return () => debouncedIqamaSetter.cancel();
  }, [iqama, debouncedIqamaSetter]);

  const debouncedEidSetter = useMemo(
    () => debounce((value: string) => setDebouncedEid(value), 500),
    []
  );

  useEffect(() => {
    debouncedEidSetter(eid || "");
    return () => debouncedEidSetter.cancel();
  }, [eid, debouncedEidSetter]);

  const debouncedPassportNumberSetter = useMemo(
    () => debounce((value: string) => setDebouncedPassportNumber(value), 500),
    []
  );

  useEffect(() => {
    debouncedPassportNumberSetter(passportNumber || "");
    return () => debouncedPassportNumberSetter.cancel();
  }, [passportNumber, debouncedPassportNumberSetter]);

  const debouncedVisaNumberSetter = useMemo(
    () => debounce((value: string) => setDebouncedVisaNumber(value), 500),
    []
  );

  useEffect(() => {
    debouncedVisaNumberSetter(visaNumber || "");
    return () => debouncedVisaNumberSetter.cancel();
  }, [visaNumber, debouncedVisaNumberSetter]);

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: location ? location.toString() : "",
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
          "s_BuildingName_En",
          "s_BuildingName_Ar",
          "n_BuildingID",
          allBuildings
        )
        : [],
    [allBuildings]
  );

  const checkedInBy = watch("checkInBy");
  const checkedOutBy = watch("checkOutBy");

  const { data: visitRequests } = useGetAllVisitorsReport({
    s_VisitorName: debouncedVisitorName || "",
    s_Sponsor: debouncedCompanyName || "",
    s_MobileNo: debouncedNumber || "",
    encryptedLocationId: location ? location?.toString() : "",
    n_BuildingID: building ? Number(building) : 0,
    ...(fromDate && { dt_FromDate: fromDate }),
    ...(toDate && { dt_ToDate: toDate }),
    n_checkInOutType: checkInOutType ? Number(checkInOutType) : 0,
    n_CheckedInBy: checkedInBy ? Number(checkedInBy) : 0,
    n_CheckedOutBy: checkedOutBy ? Number(checkedOutBy) : 0,
    VisitDurationType: Number(visitDurationType) || 0,
    eidNumber:
      selectedIdType == DOC_TYPE_ID.NATIONAL_ID.toString() ? debouncedEid : "",
    passportNumber:
      selectedIdType == DOC_TYPE_ID.PASSPORT.toString()
        ? debouncedPassportNumber
        : "",
    s_Iqama:
      selectedIdType == DOC_TYPE_ID.IQAMA.toString() ? debouncedIqama : "",
    s_VisaNo:
      selectedIdType == DOC_TYPE_ID.VISA.toString() ? debouncedVisaNumber : "",
    pageSize: pageSize,
    pageNumber: page,
    search: search,
  });

  useEffect(() => {
    setExportBody({
      s_VisitorName: debouncedVisitorName || "",
      s_Sponsor: debouncedCompanyName || "",
      s_MobileNo: debouncedNumber || "",
      encryptedLocationId: location ? location?.toString() : "",
      n_BuildingID: building ? Number(building) : 0,
      ...(fromDate && { dt_FromDate: fromDate }),
      ...(toDate && { dt_ToDate: toDate }),
      n_checkInOutType: checkInOutType ? Number(checkInOutType) : 0,
      n_CheckedInBy: checkedInBy ? Number(checkedInBy) : 0,
      n_CheckedOutBy: checkedOutBy ? Number(checkedOutBy) : 0,
      VisitDurationType: Number(visitDurationType) || 0,
      eidNumber:
        selectedIdType == DOC_TYPE_ID.NATIONAL_ID.toString()
          ? debouncedEid
          : "",
      passportNumber:
        selectedIdType == DOC_TYPE_ID.PASSPORT.toString()
          ? debouncedPassportNumber
          : "",
      s_Iqama:
        selectedIdType == DOC_TYPE_ID.IQAMA.toString() ? debouncedIqama : "",
      s_VisaNo:
        selectedIdType == DOC_TYPE_ID.VISA.toString()
          ? debouncedVisaNumber
          : "",
      pageSize: EXPORT_PAGE_SIZE,
      pageNumber: page,
      search: search,
    });
  }, [
    checkInOutType,
    debouncedCompanyName,
    debouncedNumber,
    debouncedVisitorName,
    debouncedEid,
    debouncedIqama,
    debouncedPassportNumber,
    debouncedVisaNumber,
    selectedIdType,
    fromDate,
    location,
    page,
    search,
    setExportBody,
    toDate,
    building,
    visitDurationType,
    checkedInBy,
    checkedOutBy
  ]);

  useEffect(() => {
    if (visitRequests) {
      setAllRequests(visitRequests.data || []);
      setPages(visitRequests.pagination?.pages || 1);
      setCount(visitRequests.pagination?.count || 0);
    }
  }, [setAllRequests, setPages, setCount, visitRequests]);

  const onClear = useCallback(() => {
    reset({ ...initialState, idType: DOC_TYPE_ID.NATIONAL_ID.toString() });
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 lg:gap-4">
              <Controller
                name="fullName"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("fullName")}
                    onChange={(e) => {
                      onChange(e);
                      debouncedFullNameSetter(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />

              <Controller
                name="companyName"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("companyName")}
                    onChange={(e) => {
                      onChange(e);
                      debouncedCompanyNameSetter(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />

              <Controller
                name="number"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("phoneNumber")}
                    onChange={(e) => {
                      onChange(e);
                      debouncedNumberSetter(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                    isInfoToolTip
                    infoToolTip={<p>{t("enterPhoneNumber")}</p>}
                  />
                )}
              />

              <Controller
                name="idType"
                control={control}
                render={({ field: { name, onChange, value } }) => {
                  return (
                    <ComboboxInput
                      options={documentIDTypes}
                      onChange={(value) => {
                        onChange(value === "" ? null : value);
                        setValue("iqama", "");
                        setValue("eid", "");
                        setValue("passportNumber", "");
                        setValue("visaNumber", "");
                      }}
                      error={errors?.[name] && t("idTypeRequired")}
                      value={
                        value
                          ? value == DOC_TYPE_ID.NATIONAL_ID.toString()
                            ? "National ID"
                            : value == DOC_TYPE_ID.PASSPORT.toString()
                              ? "Passport"
                              : value == DOC_TYPE_ID.IQAMA.toString()
                                ? "Iqama"
                                : value == DOC_TYPE_ID.VISA.toString()
                                  ? "Visa"
                                  : ""
                          : ""
                      }
                      label={t("idType")}
                    />
                  );
                }}
              />
              {selectedIdType == DOC_TYPE_ID.IQAMA.toString() && (
                <Controller
                  name="iqama"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("iqamaNumber")}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      value={value}
                    />
                  )}
                />
              )}

              {(!selectedIdType ||
                selectedIdType == DOC_TYPE_ID.NATIONAL_ID.toString()) && (
                  <Controller
                    name="eid"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <TextInput
                        label={t("nationalID")}
                        value={value}
                        onChange={onChange}
                        error={errors?.[name]?.message}
                      />
                    )}
                  />
                )}

              {selectedIdType == DOC_TYPE_ID.PASSPORT.toString() && (
                <Controller
                  name="passportNumber"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("passportNumber")}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      value={value}
                    />
                  )}
                />
              )}

              {selectedIdType == DOC_TYPE_ID.VISA.toString() && (
                <Controller
                  name="visaNumber"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("visaNumber")}
                      onChange={onChange}
                      error={errors?.[name]?.message}
                      value={value}
                    />
                  )}
                />
              )}

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setValue("building", "");
                    }}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />

              <Controller
                name="checkInDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("checkInDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />

              <Controller
                name="checkOutDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("checkOutDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />
              <Controller
                name="checkInOutType"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={checkInOutTypes}
                    value={
                      checkInOutTypes?.find(
                        (item) => item.value === Number(value)
                      )?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("checkInOutType")}
                  />
                )}
              />

              <Controller
                name="visitDurationType"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    label={t("visitDurationType")}
                    options={visitDurations}
                    value={
                      visitDurations?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                  />
                )}
              />

              <Controller
                name="checkInBy"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    label={t("checkedInBy")}
                    options={checkInByReceptionists}
                    value={
                      checkInByReceptionists?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                  />
                )}
              />

              <Controller
                name="checkOutBy"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    label={t("checkedOutBy")}
                    options={checkOutByReceptionists}
                    value={
                      checkOutByReceptionists?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </form>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default React.memo(SearchVisitorsReportCard);
